/**
 * What a seat is doing, in words — pure, so jest can read it without a DOM.
 *
 * The strip has always shown three states and a token count: "working" says a
 * turn is running but never what it is running ON, so a room of eight busy
 * personas told you nothing about what the dig was actually doing.
 */
import type { BoardroomAssignment } from '../../types';

/** The first few items a worklist names, for a one-line summary. */
export function itemsLabel(a: BoardroomAssignment, max = 3): string {
  const ids = a.item_ids ?? [];
  if (ids.length === 0) return '';
  const shown = ids.slice(0, max).map(i => `#${i}`).join(' ');
  const more = ids.length - Math.min(ids.length, max);
  return more > 0 ? `${shown} +${more}` : shown;
}

/** One line under the persona's name. Null when it has no worklist. */
export function workLine(a: BoardroomAssignment | null | undefined): string | null {
  if (!a) return null;
  const n = a.item_ids?.length ?? 0;
  if (n === 0) return null;
  const what = a.what ? a.what.replace(/_/g, ' ') : 'work';
  const verb = a.status === 'pending' ? 'to do' : 'on';
  return `${verb} ${n} ${n === 1 ? 'item' : 'items'} · ${what}`;
}

// --------------------------- what it has DONE ---------------------------
//
// Turns and tokens say a seat RAN. Neither says it worked, and the gap between
// the two is not hypothetical: one cartographer took 19 turns and spent 2.7M
// tokens — the most expensive seat in its room — while every `record_facts`
// call it made came back "Unknown tool", and the strip showed a healthy seat
// with a big number beside it. `writes` is successful calls by tool name.

/** Successful writes over this seat's life. */
export function writeCount(writes?: Record<string, number> | null): number {
  return Object.values(writes ?? {}).reduce((n, v) => n + (v || 0), 0);
}

/** The chip beside the token count, or null for a seat that has not run yet —
 *  "0 writes" on a seat that never had a turn is noise, not a warning. */
export function writesLabel(
  writes: Record<string, number> | null | undefined,
  turnsDone: number,
): string | null {
  if (!turnsDone) return null;
  const n = writeCount(writes);
  return `${n} ${n === 1 ? 'write' : 'writes'}`;
}

/** BAD is the one worth colouring: turns taken, nothing written. That is a seat
 *  spending money on nothing, and it is the state that went unnoticed for a
 *  whole dig. */
export function writesTone(
  writes: Record<string, number> | null | undefined,
  turnsDone: number,
): 'bad' | 'quiet' {
  return turnsDone > 0 && writeCount(writes) === 0 ? 'bad' : 'quiet';
}

/** The breakdown, biggest first, for the tooltip. The per-tool split is the
 *  point: a total of 41 tells you the seat worked, and only the split tells you
 *  WHICH of its jobs it did — or, for a broken tool, never once did. */
export function writesTitle(
  writes: Record<string, number> | null | undefined,
  turnsDone: number,
): string {
  const rows = Object.entries(writes ?? {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  if (!rows.length) {
    return turnsDone > 0
      ? `${turnsDone} ${turnsDone === 1 ? 'turn' : 'turns'} and nothing written`
      : 'has not taken a turn yet';
  }
  return ['successful calls:', ...rows.map(([tool, n]) => `${tool} ${n}`)].join('\n');
}

/** Where a worklist came from, for the tooltip. A person's own instruction
 *  should be legible as theirs rather than looking like the room's idea. */
export function sourceLabel(a: BoardroomAssignment): string {
  return a.source === 'human'
    ? 'you asked for this'
    : a.source === 'director'
      ? 'the room chose this'
      : 'from what the tables say is missing';
}
